// Configuración central de rutas y roles por defecto.
// Sirve como fuente única de verdad para:
// - Filtrado del sidebar (DashboardLayout)
// - Redirección post-login (LoginPage)
// - Guards de ruta (ProtectedRoute en App.tsx)

export interface MenuItem {
  path: string;
  label: string;
  icon: string;
  roles: string[];
  /** Si es true, se usa como landing por defecto al loguearse (el primer match ganará) */
  defaultFor?: string[];
}

export const MENU_ITEMS: MenuItem[] = [
  {
    path: '/admin',
    label: 'Dashboard',
    icon: '📊',
    roles: ['sys_admin', 'general_manager'],
    defaultFor: ['sys_admin', 'general_manager'],
  },
  {
    path: '/users',
    label: 'Personal',
    icon: '👥',
    roles: ['sys_admin'],
  },
  {
    path: '/clients',
    label: 'Clientes',
    icon: '👤',
    roles: ['sys_admin', 'general_manager', 'receptionist', 'accountant'],
    defaultFor: ['receptionist'],
  },
  {
    path: '/projects',
    label: 'Proyectos',
    icon: '📋',
    roles: ['sys_admin', 'general_manager', 'project_lead', 'accountant', 'project_lead'],
    defaultFor: ['project_lead'],
  },
  {
    path: '/advances',
    label: 'Avances',
    icon: '📈',
    roles: ['sys_admin', 'general_manager', 'project_lead', 'contractor'],
    defaultFor: ['contractor'],
  },
  {
    path: '/documents',
    label: 'Documentos',
    icon: '📄',
    roles: ['sys_admin', 'general_manager', 'project_lead', 'contractor', 'data_entry', 'client'],
  },
  {
    path: '/finance',
    label: 'Finanzas',
    icon: '💰',
    roles: ['sys_admin', 'general_manager', 'accountant'],
    defaultFor: ['accountant'],
  },
  {
    path: '/manual-capture',
    label: 'Captura Manual',
    icon: '✍️',
    roles: ['sys_admin', 'general_manager', 'data_entry'],
    defaultFor: ['data_entry'],
  },
  {
    path: '/internal',
    label: 'Interno',
    icon: '🔄',
    roles: ['sys_admin', 'general_manager', 'accountant', 'receptionist'],
  },
  {
    path: '/balances',
    label: 'Saldos',
    icon: '💼',
    roles: ['sys_admin', 'general_manager', 'accountant'],
  },
  {
    path: '/admin/tokens',
    label: 'Tokens',
    icon: '💎',
    roles: ['sys_admin'],
  },
  {
    path: '/admin/catalogs',
    label: 'Catálogos',
    icon: '📚',
    roles: ['sys_admin'],
  },
  {
    path: '/admin/receipts',
    label: 'Comprobantes',
    icon: '📎',
    roles: ['sys_admin', 'general_manager', 'accountant'],
  },
  {
    path: '/messages',
    label: 'Mensajes',
    icon: '💬',
    roles: ['sys_admin', 'general_manager', 'project_lead'],
  },


  {
    path: '/etl',
    label: 'Ingesta de Datos',
    icon: '📥',
    roles: ['sys_admin', 'general_manager'],
  },
  {
    path: '/legacy-import',
    label: 'Migración Legacy',
    icon: '📥',
    roles: ['sys_admin'],
  },


  {
    path: '/settings',
    label: 'Configuración',
    icon: '⚙️',
    roles: ['sys_admin', 'general_manager'],
  },
];

/**
 * 💎 Devuelve la ruta landing por defecto para un rol.
 * Si el rol no tiene una ruta marcada como defaultFor, devuelve la primera
 * ruta del menú a la que tenga acceso. Si no tiene acceso a ninguna,
 * devuelve '/login' (fallback seguro).
 */
export function getDefaultRouteForRole(role: string): string {
  if (!role) return '/login';
  const normalized = role.trim().toLowerCase();

  // 1. Buscar una ruta explícitamente marcada como default para ese rol
  const explicit = MENU_ITEMS.find(item => item.defaultFor?.includes(normalized));
  if (explicit) return explicit.path;

  // 2. Fallback: primera ruta accesible por el rol
  const fallback = MENU_ITEMS.find(item => item.roles.includes(normalized));
  if (fallback) return fallback.path;

  // 3. Último recurso: login (para que no quede en una ruta sin permisos)
  return '/login';
}

/**
 * Filtra el menú según el rol del usuario.
 */
export function getMenuItemsForRole(role: string): MenuItem[] {
  if (!role) return [];
  const normalized = role.trim().toLowerCase();
  return MENU_ITEMS.filter(item => item.roles.includes(normalized));
}